import React from 'react';
import type { WorkflowAssignment } from '../../types';
import { computeEventIntervals, eventTiming } from './eventPriceIntervals';

/**
 * Compact "price into and out of the event" strip for a task — event date,
 * since-pitch return (assignment date → latest close, always real), anchor
 * price, and the three window returns (run-up T-10→T-2, print reaction
 * T-2→T+2, follow-through T+2→T+10). Windows that haven't printed yet render
 * as an em dash with a "Prints <date>" tooltip.
 */

const fmtReturn = (value: number | null): string =>
  value == null ? '—' : `${value >= 0 ? '+' : ''}${(value * 100).toFixed(1)}%`;

const tone = (value: number | null): string => {
  if (value == null) return 'border-neutral-200 bg-neutral-100 text-neutral-500';
  if (value > 0) return 'border-success-300 bg-success-50 text-success-700';
  if (value < 0) return 'border-danger-300 bg-danger-50 text-danger-700';
  return 'border-neutral-200 bg-neutral-100 text-neutral-600';
};

const fmtDate = (value?: string | null): string => {
  if (!value) return '—';
  const d = new Date(value);
  return Number.isNaN(d.getTime())
    ? value
    : d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
};

const Stat: React.FC<{ label: string; title?: string; children: React.ReactNode }> = ({
  label,
  title,
  children,
}) => (
  <div className="flex items-center gap-1.5" title={title}>
    <span className="text-[11px] uppercase tracking-wide text-neutral-400">{label}</span>
    {children}
  </div>
);

const ReturnChip: React.FC<{ value: number | null }> = ({ value }) => (
  <span className={`inline-flex items-center rounded-md border px-2 py-0.5 text-xs font-semibold ${tone(value)}`}>
    {fmtReturn(value)}
  </span>
);

interface EventPriceHeaderProps {
  assignment: Pick<WorkflowAssignment, 'eventDate' | 'eventPriceData'>;
}

const EventPriceHeader: React.FC<EventPriceHeaderProps> = ({ assignment }) => {
  const data = assignment.eventPriceData;
  const hasSincePitch = typeof data?.sincePitchReturn === 'number';
  if (!data || (!data.anchorPrice && !(data.priceTable?.length) && !hasSincePitch)) return null;

  const timing = eventTiming(assignment.eventDate);
  // Reaction/post windows are only honest once the print exists; the run-up
  // may show while imminent. Pre-print they dash with a "Prints" tooltip.
  const intervals = computeEventIntervals(data);
  const into = timing === 'past' || timing === 'imminent' ? intervals.into : null;
  const reaction = timing === 'past' ? intervals.reaction : null;
  const post = timing === 'past' ? intervals.post : null;
  const prints =
    timing === 'imminent' || timing === 'future'
      ? `Prints ${fmtDate(assignment.eventDate)}`
      : undefined;

  return (
    <div className="flex flex-wrap items-center gap-x-5 gap-y-2 rounded-xl border border-neutral-200 bg-surface px-4 py-2.5 shadow-sm">
      <Stat label="Event">
        <span className="text-sm font-semibold text-neutral-800">{fmtDate(assignment.eventDate)}</span>
      </Stat>
      {hasSincePitch && (
        <Stat label="Since Pitch" title="Assignment date → latest close">
          <ReturnChip value={data.sincePitchReturn!} />
        </Stat>
      )}
      <Stat label="Event Px" title="Close on the event date (nearest prior trading day)">
        <span className="text-sm font-semibold text-neutral-800">
          {typeof data.anchorPrice === 'number' ? data.anchorPrice.toFixed(2) : '—'}
        </span>
      </Stat>
      <Stat label="Into Print" title={into == null && prints ? prints : 'T-10 → T-2 trading days'}>
        <ReturnChip value={into} />
      </Stat>
      <Stat label="Reaction" title={reaction == null && prints ? prints : 'T-2 → T+2 trading days'}>
        <ReturnChip value={reaction} />
      </Stat>
      <Stat label="Post" title={post == null && prints ? prints : 'T+2 → T+10 trading days'}>
        <ReturnChip value={post} />
      </Stat>
    </div>
  );
};

export default EventPriceHeader;
